import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Users as UsersIcon,
  UserPlus,
  RefreshCw,
  Pencil,
  Trash2,
  Check,
  X,
  Key,
  Eye,
  Plus,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiService, eventsApi, adminApi } from '../../services/api';
import { IUser, IApiResponse, Event, AppUser } from '../../types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { COUNTRY_CODES, splitPhone } from '../../config';

interface UserEventConnection {
  eventId: string;
  eventName: string;
  sessionCount: number;
  firstJoined: string;
  lastActive: string;
  messageCount: number;
}

interface FormValues {
  name: string;
  email: string;
  password: string;
  phone: string;
  phoneCountryCode: string;
  occupation: string;
  businessNature: string;
  businessDetails: string;
  preferences: string;
  eventId: string;
}

const PAGE_SIZE = 10;

const Users: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Data state
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [events, setEvents] = useState<Event[]>([]);
  const [clients, setClients] = useState<AppUser[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [userStats, setUserStats] = useState({ totalUsers: 0, recentUsers: 0 });
  const [loadingStats, setLoadingStats] = useState(false);

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [selectedEventId, setSelectedEventId] = useState<string>(() => (isAdmin ? 'all' : ''));
  const [selectedClientId, setSelectedClientId] = useState('');

  // Edit modal state
  const [editingUser, setEditingUser] = useState<IUser | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({
    name: '', email: '', password: '', phone: '', phoneCountryCode: '+91',
    occupation: '', businessNature: '', businessDetails: '', preferences: '{}', eventId: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<FormValues>>({});
  const [saving, setSaving] = useState(false);

  // Delete confirm state
  const [userToDelete, setUserToDelete] = useState<IUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Reset password state
  const [resetPasswordUser, setResetPasswordUser] = useState<IUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetPasswordErrors, setResetPasswordErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});
  const [resetting, setResetting] = useState(false);

  // Phone conflict modal
  const [phoneConflictInfo, setPhoneConflictInfo] = useState<{
    userName: string;
    userEmail: string;
    eventName: string;
  } | null>(null);

  // User events modal
  const [userEventsUser, setUserEventsUser] = useState<IUser | null>(null);
  const [userEventConnections, setUserEventConnections] = useState<UserEventConnection[]>([]);
  const [loadingUserEvents, setLoadingUserEvents] = useState(false);

  // Debounce ref
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Sync page from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const p = parseInt(params.get('page') || '1', 10);
    setPage(isNaN(p) ? 1 : p);
  }, [location.search]);

  useEffect(() => {
    if (isAdmin && clients.length === 0) fetchClients();
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchEvents();
  }, [isAdmin, selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAdmin && !selectedEventId) {
      setSelectedEventId('all');
    }
  }, [isAdmin, selectedEventId]);

  useEffect(() => {
    fetchUsers();
  }, [page, searchText, selectedEventId, selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUserStats();
  }, [selectedEventId, selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    if (!isAdmin && !selectedEventId) {
      setUsers([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const params: any = { page, limit: PAGE_SIZE };
      if (searchText) params.search = searchText;
      if (isAdmin) {
        if (selectedClientId) params.clientId = selectedClientId;
        if (selectedEventId && selectedEventId !== 'all') {
          const ev = events.find(e => e._id === selectedEventId);
          if (ev) { params.eventId = ev.eventId; delete params.clientId; }
        }
      }
      const response: IApiResponse<IUser[]> = await apiService.get('/users', { params });
      if (response.success && response.data) {
        setUsers(response.data || []);
        setTotal(response.pagination?.total || 0);
      }
    } catch (error: any) {
      if (error?.code !== 'ERR_NETWORK') toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (response.data.success && response.data.data) {
        setClients(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const params: any = { limit: 100 };
      if (isAdmin && selectedClientId) params.clientId = selectedClientId;
      else if (!isAdmin && user?._id) params.clientId = user._id;
      const response = await eventsApi.getAll(params);
      if (response.data?.data) setEvents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchUserStats = async () => {
    if (!isAdmin && !selectedEventId) {
      setUserStats({ totalUsers: 0, recentUsers: 0 });
      return;
    }

    setLoadingStats(true);
    try {
      const params: any = {};
      if (isAdmin && selectedClientId) params.clientId = selectedClientId;
      if (selectedEventId && selectedEventId !== 'all') {
        const ev = events.find(e => e._id === selectedEventId);
        if (ev) params.eventId = ev.eventId;
      }
      const response: IApiResponse<any> = await apiService.get('/users/statistics', { params });
      if (response.success && response.data) {
        setUserStats({
          totalUsers: response.data.totalUsers || 0,
          recentUsers: response.data.newUsers || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleSearchChange = (value: string) => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchText(value);
      setPage(1);
    }, 300);
  };

  const handleClientFilterChange = (clientId: string) => {
    setSelectedClientId(clientId);
    setSelectedEventId('all');
  };

  const handleEventFilterChange = (eventId: string) => {
    setSelectedEventId(eventId);
    setPage(1);
  };

  // Edit
  const openEditModal = (u: IUser) => {
    setEditingUser(u);
    const { countryCode, localNumber } = splitPhone((u as any).phone || '');
    setFormValues({
      name: u.name,
      email: u.email || '',
      password: '',
      phone: localNumber,
      phoneCountryCode: countryCode,
      occupation: u.occupation || '',
      businessNature: u.businessNature || '',
      businessDetails: u.businessDetails || '',
      preferences: u.preferences ? JSON.stringify(u.preferences, null, 2) : '{}',
      eventId: '',
    });
    setFormErrors({});
    setIsEditModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormValues({ name: '', email: '', password: '', phone: '', phoneCountryCode: '+91', occupation: '', businessNature: '', businessDetails: '', preferences: '{}', eventId: '' });
    setFormErrors({});
    setIsEditModalOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: Partial<FormValues> = {};
    if (!formValues.name.trim()) errors.name = 'Name is required';
    if (!editingUser && !formValues.email.trim()) errors.email = 'Email is required';
    else if (formValues.email.trim() && !/\S+@\S+\.\S+/.test(formValues.email)) errors.email = 'Invalid email';
    if (!editingUser && !formValues.password) errors.password = 'Password is required';
    if (!editingUser && formValues.password && formValues.password.length < 6) errors.password = 'At least 6 characters';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveUser = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      let preferences = {};
      if (formValues.preferences) {
        try { preferences = JSON.parse(formValues.preferences); }
        catch { toast.error('Invalid JSON for preferences'); setSaving(false); return; }
      }
      const fullPhone = formValues.phone.trim()
        ? `${formValues.phoneCountryCode.replace('+', '')}${formValues.phone.trim()}`
        : undefined;
      const { phoneCountryCode: _cc, ...rest } = { ...formValues, preferences };
      const userData = { ...rest, phone: fullPhone };

      let response: IApiResponse<IUser>;
      if (editingUser) {
        response = await apiService.put(`/users/${editingUser.userId}`, userData);
      } else {
        response = await apiService.post('/users', userData);
      }

      if (response.success) {
        toast.success(`User ${editingUser ? 'updated' : 'created'} successfully`);
        setIsEditModalOpen(false);
        fetchUsers();
        fetchUserStats();
      } else {
        toast.error(response.message || 'Failed to save user');
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 409 && data?.data?.conflict) {
        setPhoneConflictInfo(data.data.conflict);
        return;
      }
      const msg = data?.message || data?.error || error?.message;
      toast.error(msg || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      const response: IApiResponse<any> = await apiService.delete(`/users/${userToDelete.userId}`);
      if (response.success) {
        toast.success('User deleted successfully');
        setUserToDelete(null);
        fetchUsers();
        fetchUserStats();
      } else {
        toast.error(response.message || 'Failed to delete user');
      }
    } catch (error: any) {
      toast.error('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  // Reset password
  const validateResetPassword = (): boolean => {
    const errors: { newPassword?: string; confirmPassword?: string } = {};
    if (!newPassword) errors.newPassword = 'Password is required';
    else if (newPassword.length < 6) errors.newPassword = 'At least 6 characters';
    if (!confirmPassword) errors.confirmPassword = 'Please confirm password';
    else if (newPassword !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    setResetPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleResetPasswordSave = async () => {
    if (!validateResetPassword() || !resetPasswordUser) return;
    setResetting(true);
    try {
      const response: IApiResponse<any> = await apiService.put(
        `/users/${resetPasswordUser.userId}/reset-password`,
        { newPassword }
      );
      if (response.success) {
        toast.success('Password reset successfully');
        setResetPasswordUser(null);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(response.message || 'Failed to reset password');
      }
    } catch (error: any) {
      toast.error('Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  // User events
  const handleViewUserEvents = async (u: IUser) => {
    setUserEventsUser(u);
    setLoadingUserEvents(true);
    setUserEventConnections([]);
    try {
      let allSessions: any[] = [];
      let currentPage = 1;
      let totalPages = 1;
      while (currentPage <= totalPages) {
        const resp: IApiResponse<any[]> = await apiService.get(`/users/${u.userId}/sessions`, {
          params: { page: currentPage, limit: 100 },
        });
        if (resp.success && resp.data) {
          allSessions = allSessions.concat(resp.data);
          totalPages = resp.pagination?.pages || 1;
          currentPage++;
        } else break;
      }

      // Filter to only sessions for this user's specific event — the same email may
      // exist in other events/clients and we must not show those here.
      const eventSessions = u.eventId
        ? allSessions.filter(s => s.eventId === u.eventId)
        : allSessions;

      const eventMap = new Map<string, UserEventConnection>();
      for (const session of eventSessions) {
        const evId = session.eventId;
        const ev = events.find(e => e._id === evId);
        const evName = ev?.name || 'Unknown Event';
        if (!eventMap.has(evId)) {
          eventMap.set(evId, { eventId: evId, eventName: evName, sessionCount: 0, firstJoined: session.createdAt, lastActive: session.updatedAt, messageCount: 0 });
        }
        const conn = eventMap.get(evId)!;
        conn.sessionCount++;
        conn.messageCount += session.conversations?.length || 0;
        if (new Date(session.createdAt) < new Date(conn.firstJoined)) conn.firstJoined = session.createdAt;
        if (new Date(session.updatedAt) > new Date(conn.lastActive)) conn.lastActive = session.updatedAt;
      }
      setUserEventConnections(Array.from(eventMap.values()));
    } catch (error) {
      toast.error('Failed to load user events');
    } finally {
      setLoadingUserEvents(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const goToPage = (p: number) => {
    const params = new URLSearchParams(location.search);
    params.set('page', String(p));
    navigate({ search: params.toString() });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const selectedEvent = events.find((event) => event._id === selectedEventId);
  const shouldShowBlankClientState = !isAdmin && !selectedEventId;
  const showVerifiedColumn = Boolean(selectedEvent?.requiresEmailVerification);
  const tableColumnCount = showVerifiedColumn ? 9 : 8;

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
        <p className="text-sm text-gray-500 mt-1">Manage and monitor user activity</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        {isAdmin && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Filter by Client:</label>
            <select
              value={selectedClientId}
              onChange={(e) => handleClientFilterChange(e.target.value)}
              disabled={loadingClients}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            >
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c._id} value={c._id}>{c.name} ({c.email})</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Filter by Knowledge Assistant:
          </label>
          <select
            value={selectedEventId}
            onChange={(e) => handleEventFilterChange(e.target.value)}
            disabled={events.length === 0}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[250px]"
          >
            <option value="">Select Knowledge Assistant</option>
            {isAdmin && <option value="all">All Knowledge Assistants</option>}
            {events.map(ev => (
              <option key={ev._id} value={ev._id}>{ev.name}</option>
            ))}
          </select>
        </div>
      </div>

      {shouldShowBlankClientState ? (
        <div className="bg-white border border-gray-200 rounded-lg px-6 py-16 text-center">
          <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">Select a Knowledge Assistant</p>
          <p className="text-sm text-gray-500 mt-1">Choose a Knowledge Assistant above to load user cards and table data.</p>
        </div>
      ) : (
        <>
          {/* Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Users (till date)</p>
                  <p className="text-3xl font-semibold text-gray-900">
                    {loadingStats ? '—' : userStats.totalUsers.toLocaleString()}
                  </p>
                </div>
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <UsersIcon className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">New Users (Last 7 days)</p>
                  <p className="text-3xl font-semibold text-gray-900">
                    {loadingStats ? '—' : userStats.recentUsers.toLocaleString()}
                  </p>
                </div>
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Users Table Card */}
          <div className="bg-white border border-gray-200 rounded-lg">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Users Management</h2>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder="Search by name or email"
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-64"
                    />
                  </div>
                  <button
                    onClick={() => { fetchUsers(); fetchUserStats(); }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </button>
                  {isAdmin && (
                    <button
                      onClick={openCreateModal}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add User
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    {showVerifiedColumn && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified</th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occupation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Nature</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={tableColumnCount} className="px-6 py-16 text-center text-sm text-gray-400">
                        Loading...
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={tableColumnCount} className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <UsersIcon className="w-12 h-12 text-gray-300 mb-3" />
                          <p className="text-gray-500 text-sm">No users found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.userId} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{u.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.email || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {(u as any).phone ? (() => { const { countryCode, localNumber } = splitPhone((u as any).phone); return localNumber ? `${countryCode} ${localNumber}` : (u as any).phone; })() : '-'}
                        </td>
                        {showVerifiedColumn && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {u.emailVerificationStatus === 'verified' ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <X className="w-4 h-4 text-red-400" />
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.occupation || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.businessNature || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(u.createdAt)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(u.updatedAt)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleViewUserEvents(u)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="View Knowledge Assistants"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => openEditModal(u)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit user"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => { setResetPasswordUser(u); setNewPassword(''); setConfirmPassword(''); setResetPasswordErrors({}); }}
                                  className="p-2 text-orange-500 hover:bg-orange-50 rounded transition-colors"
                                  title="Reset password"
                                >
                                  <Key className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setUserToDelete(u)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {total > 0 ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total} users` : '0 users'}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1}
                    className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const p = totalPages <= 5 ? i + 1 : Math.max(1, page - 2) + i;
                    if (p > totalPages) return null;
                    return (
                      <button
                        key={p}
                        onClick={() => goToPage(p)}
                        className={`px-3 py-1 text-sm rounded transition-colors ${
                          p === page
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= totalPages}
                    className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <span className="ml-2 text-sm text-gray-600">{PAGE_SIZE} / page</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit / Create User Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsEditModalOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingUser ? 'Edit User' : 'Add User'}
                </h2>
                {editingUser && (
                  <p className="text-sm text-gray-500 mt-0.5">Update details for {editingUser.name}</p>
                )}
              </div>
              <button onClick={() => setIsEditModalOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formValues.name}
                  onChange={(e) => setFormValues(v => ({ ...v, name: e.target.value }))}
                  placeholder="Enter user name"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.name ? 'border-red-400' : 'border-gray-300'}`}
                />
                {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={formValues.email}
                  onChange={(e) => setFormValues(v => ({ ...v, email: e.target.value }))}
                  placeholder="Enter email address"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.email ? 'border-red-400' : 'border-gray-300'}`}
                />
                {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                <div className="flex gap-2">
                  <select
                    value={formValues.phoneCountryCode}
                    onChange={(e) => setFormValues(v => ({ ...v, phoneCountryCode: e.target.value }))}
                    className="rounded-lg border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.code} {c.name}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={formValues.phone}
                    onChange={(e) => setFormValues(v => ({ ...v, phone: e.target.value.replace(/\D/g, '') }))}
                    placeholder="10-digit number"
                    className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.phone ? 'border-red-400' : 'border-gray-300'}`}
                  />
                </div>
                {formErrors.phone && <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>}
                <p className="text-xs text-gray-400 mt-1">Required for WhatsApp-enabled assistants.</p>
              </div>

              {/* Password (create only) */}
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Password <span className="text-red-500">*</span></label>
                  <input
                    type="password"
                    value={formValues.password}
                    onChange={(e) => setFormValues(v => ({ ...v, password: e.target.value }))}
                    placeholder="Minimum 6 characters"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.password ? 'border-red-400' : 'border-gray-300'}`}
                  />
                  {formErrors.password && <p className="text-xs text-red-500 mt-1">{formErrors.password}</p>}
                </div>
              )}

              {/* Occupation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Occupation</label>
                <input
                  type="text"
                  value={formValues.occupation}
                  onChange={(e) => setFormValues(v => ({ ...v, occupation: e.target.value }))}
                  placeholder="e.g. Software Engineer, Student, Business Owner"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Business Nature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Nature / Industry</label>
                <input
                  type="text"
                  value={formValues.businessNature}
                  onChange={(e) => setFormValues(v => ({ ...v, businessNature: e.target.value }))}
                  placeholder="e.g. Technology, Healthcare, Education"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Business Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Details</label>
                <textarea
                  value={formValues.businessDetails}
                  onChange={(e) => setFormValues(v => ({ ...v, businessDetails: e.target.value }))}
                  rows={3}
                  placeholder="Additional context about the user's business"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Assign to event (create only) */}
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Assign to Knowledge Assistant (Optional)</label>
                  <select
                    value={formValues.eventId}
                    onChange={(e) => setFormValues(v => ({ ...v, eventId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">None</option>
                    {events.map(ev => (
                      <option key={ev._id} value={ev.eventId}>{ev.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
                >
                  {saving ? 'Saving...' : editingUser ? 'Save Changes' : 'Add User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phone Number Conflict Modal */}
      {phoneConflictInfo && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setPhoneConflictInfo(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 mb-1">Phone Number Already in Use</h3>
                <p className="text-sm text-gray-600 mb-4">
                  This phone number is already registered to another user. The edit cannot be saved.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  <p className="text-xs font-medium text-amber-800 uppercase tracking-wide mb-2">Registered to</p>
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">User:</span> {phoneConflictInfo.userName}
                  </p>
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">Email:</span> {phoneConflictInfo.userEmail}
                  </p>
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">Knowledge Assistant:</span> {phoneConflictInfo.eventName}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setPhoneConflictInfo(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                OK, Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setUserToDelete(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Delete User</h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete <span className="font-medium">{userToDelete.name}</span>?
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  This action cannot be undone and will also delete all associated sessions.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setUserToDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                No, Keep User
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setResetPasswordUser(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Reset Password</h2>
              <button onClick={() => setResetPasswordUser(null)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Resetting password for <span className="font-medium">{resetPasswordUser.name}</span> ({resetPasswordUser.email})
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  autoComplete="new-password"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${resetPasswordErrors.newPassword ? 'border-red-400' : 'border-gray-300'}`}
                />
                {resetPasswordErrors.newPassword && <p className="text-xs text-red-500 mt-1">{resetPasswordErrors.newPassword}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${resetPasswordErrors.confirmPassword ? 'border-red-400' : 'border-gray-300'}`}
                />
                {resetPasswordErrors.confirmPassword && <p className="text-xs text-red-500 mt-1">{resetPasswordErrors.confirmPassword}</p>}
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setResetPasswordUser(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPasswordSave}
                  disabled={resetting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
                >
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Events Modal */}
      {userEventsUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setUserEventsUser(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Knowledge Assistant Connections — {userEventsUser.name}</h2>
              <button onClick={() => setUserEventsUser(null)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6">
              {loadingUserEvents ? (
                <div className="py-12 text-center text-sm text-gray-400">Loading Knowledge Assistants...</div>
              ) : userEventConnections.length === 0 ? (
                <div className="py-12 text-center">
                  <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">This user has not joined any Knowledge Assistants yet.</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-500 mb-4">Connected to {userEventConnections.length} Knowledge Assistant(s):</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Knowledge Assistant</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Sessions</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Messages</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">First Joined</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {userEventConnections.map((conn) => (
                          <tr key={conn.eventId} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-900">{conn.eventName}</td>
                            <td className="px-4 py-3 text-center text-gray-700">{conn.sessionCount}</td>
                            <td className="px-4 py-3 text-center text-gray-700">{conn.messageCount}</td>
                            <td className="px-4 py-3 text-gray-500">{new Date(conn.firstJoined).toLocaleString()}</td>
                            <td className="px-4 py-3 text-gray-500">{new Date(conn.lastActive).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 px-6 py-3 flex justify-end">
              <button
                onClick={() => setUserEventsUser(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
